import { useCallback, useEffect, useRef } from "react";

/**
 * Whether the calling component is still mounted, read through a getter.
 *
 * Long-running work outlives the view that starts it. Applicant convergence keeps
 * probing and refetching for whichever list is watching now, which is the point —
 * an employer who moves a candidate and immediately goes back to the list is the
 * one who needs those rows. The consequence is that the promise a click handler
 * awaits can settle after that handler's component has gone. Clearing its loading
 * flag then is pointless, and React 17 warns about it, so every state update after
 * an `await` on an op is guarded with this.
 *
 * A getter rather than the ref itself so call sites read as a condition and cannot
 * accidentally capture a stale boolean.
 *
 * Mounted from the first render, not from the mount effect. `usehooks-ts` exports a
 * hook of the same name that starts `false` and only flips in an effect, so it
 * reports "unmounted" for a component that is merely mid-first-render — which turns
 * a guard into a silent skip, and in `HirePipeline/queries.ts` into a thrown error.
 * Nothing here wants that reading: the question is "has this unmounted", and before
 * the first commit the answer is no.
 */
export const useIsMounted = (): (() => boolean) => {
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  return useCallback(() => mounted.current, []);
};
